import { Box, Flex, Text } from '@chakra-ui/react';
import type { ConversationAttachment, MessageData } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { useConversationAttachmentUrlQuery } from '@/hooks/queries/use-conversation-attachment-url-query';
import { LinkedText } from './LinkedText';
import MessageContainer from './MessageContainer';

/** The files a message carries, as the backend stores them. */
export function readMessageAttachments(metadata: unknown): ConversationAttachment[] {
  const list = (metadata as { attachments?: unknown } | null)?.attachments;
  return Array.isArray(list)
    ? list.filter(
        (entry): entry is ConversationAttachment =>
          Boolean(entry) && typeof entry.mediaId === 'string' && typeof entry.fileName === 'string'
      )
    : [];
}

function AttachmentFile({
  conversationId,
  attachment,
}: {
  conversationId: string;
  attachment: ConversationAttachment;
}) {
  const { t } = useTranslation();
  const link = useConversationAttachmentUrlQuery(conversationId, attachment.mediaId);

  if (link.isError) {
    return (
      <Text fontSize="13px" color="fg.muted">
        {t('messages.attachment.unavailable')}
      </Text>
    );
  }
  if (!link.data) return <Box w="160px" h="40px" />;

  if (attachment.kind === 'image') {
    return (
      <a
        href={link.data.url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('messages.attachment.openImage')}
      >
        <img
          src={link.data.url}
          alt={attachment.fileName}
          onError={() => void link.refetch()}
          style={{ maxWidth: 240, maxHeight: 240, borderRadius: 18, display: 'block' }}
        />
      </a>
    );
  }

  return (
    <Flex align="center" gap={3}>
      <Text fontSize="14px" fontWeight="500" truncate maxW="200px">
        {attachment.fileName}
      </Text>
      <a href={link.data.url} target="_blank" rel="noopener noreferrer">
        <Text as="span" fontSize="13px" textDecoration="underline">
          {t('messages.attachment.download')}
        </Text>
      </a>
    </Flex>
  );
}

/**
 * A file a creator sent: a photo shown at 240px at most, or a PDF to download.
 * Read through a short signed link, never a public address.
 */
const AttachmentMessage: React.FC<{ message: MessageData; time: string }> = ({ message, time }) => {
  const attachments = readMessageAttachments(message.metadata);
  return (
    <MessageContainer
      time={time}
      bg="primary.900"
      color="primary.50"
      data-message-tone="creator"
      px={3.5}
      py={2.5}
      rounded="18px"
      isSelf
    >
      <Flex direction="column" gap={2}>
        {attachments.map((attachment) => (
          <AttachmentFile
            key={attachment.mediaId}
            conversationId={message.conversationId}
            attachment={attachment}
          />
        ))}
        {message.content ? (
          <Text fontSize="14px" lineHeight="20px">
            <LinkedText text={message.content} />
          </Text>
        ) : null}
      </Flex>
    </MessageContainer>
  );
};

export default AttachmentMessage;
